import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL, counterpartyApi, isinMasterApi, api } from '../services/api';
import DocumentUpload from '../components/DocumentUpload';
import DocumentList from '../components/DocumentList';
import '../styles/FixedDepositPage.css';
import BackOfficeFinalFixedDepositBlotter from './BackOfficeFinalFixedDepositBlotter';

const FixedDepositPage = () => {
  // Get user role from localStorage
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const userRole = user?.role || '';
  const isAuthorizer = userRole === 'back_office_final' || userRole === 'authorizer';

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [counterparties, setCounterparties] = useState([]);
  const [portfolios, setPortfolios] = useState([]);
  const [settlementModes, setSettlementModes] = useState([]);
  const [isinList, setIsinList] = useState([]);
  const [investmentApprovers, setInvestmentApprovers] = useState([]);
  const [requestCounter, setRequestCounter] = useState(1);
  const [createdFixedDepositRequestId, setCreatedFixedDepositRequestId] = useState(null);
  const [showFixedDepositDocumentSection, setShowFixedDepositDocumentSection] = useState(false);
  const [selectedRequestForDocuments, setSelectedRequestForDocuments] = useState(null);
  const [fundMovementSources, setFundMovementSources] = useState([]);
  const [fundMovementSourcesLoading, setFundMovementSourcesLoading] = useState(false);
  const [recentDealsFilterDate, setRecentDealsFilterDate] = useState(() => new Date());

  // Form state
  const [formData, setFormData] = useState({
    // Request Details - default portfolio Sherwood (auto-matched once master loads).
    portfolio: 'Sherwood',
    book: '',
    module: '',
    requestNo: '',
    fileNumber: '',
    status: '',
    
    // Issuer / Counterparty Details
    counterpartyType: '',
    counterpartyName: '',
    contactPerson: '',
    requestRemarks: '',
    
    // Investment Details
    instrumentType: '',
    isin: '',
    currency: 'LKR',
    requestedAmount: '',
    targetYield: '',
    valueDate: null,
    maturityDate: null,
    dayBasis: 364,

    // Fund Movement - default Yes per ops convention
    fundMovement: 'yes',
    fundMovementType: '',
    partAmountCash: '',
    partAmountFromSources: '',
    settlementAccountCode: '',
    fundSourceDealIds: [],
    
    // Approval Workflow
    approverId: '',
    approverName: '',
    approverDesignation: '',
    approvalLimitRequired: '',
    approverNotes: ''
  });

  // Fetch initial data
  useEffect(() => {
    fetchCounterparties();
    fetchPortfolios();
    fetchSettlementModes();
    fetchIsins();
    fetchInvestmentApprovers();
    generateRequestNumber();
    fetchRequests();
  }, []);

  // Fetch fund movement sources when Part Amount is selected
  useEffect(() => {
    if (formData.fundMovement === 'yes' && formData.fundMovementType === 'part_amount') {
      fetchFundMovementSources();
    }
  }, [formData.fundMovement, formData.fundMovementType, formData.counterpartyName]);

  const fetchCounterparties = async () => {
    try {
      // Use Issuer Master as the source for counterparties
      const response = await axios.get(`${API_BASE_URL}/issuer-master`);
      setCounterparties(response.data || []);
    } catch (error) {
      console.error('Error fetching issuers for counterparty list:', error);
      setCounterparties([]);
    }
  };

  const fetchPortfolios = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/portfolio-master`);
      // Normalize to { id, name } shape as in GSec product
      const portfolios = (response.data || []).map(p => ({
        id: p.portfolio_id,
        name: p.portfolio_name
      }));
      setPortfolios(portfolios);

      // Default Portfolio = Sherwood (id-literal first, fuzzy fallback).
      setFormData((prev) => {
        if (prev.portfolio && prev.portfolio !== 'Sherwood') return prev;
        const literal = portfolios.find((p) => String(p.id) === 'Sherwood');
        const fuzzy = literal ||
          portfolios.find((p) => `${p.id || ''} ${p.name || ''}`.toLowerCase().includes('sherwood'));
        return fuzzy ? { ...prev, portfolio: fuzzy.id } : prev;
      });
    } catch (error) {
      console.error('Error fetching portfolios:', error);
      setPortfolios([]);
    }
  };

  const fetchSettlementModes = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/payment-master`);
      const raw = response.data;
      const list = Array.isArray(raw) ? raw : (raw?.data || []);
      const modes = Array.isArray(list) ? list : [];
      setSettlementModes(modes);

      // Default Settlement Account Code = first Seylan Bank entry.
      setFormData((prev) => {
        if (prev.settlementAccountCode) return prev;
        const containsSeylanBank = (m) => {
          const haystack = [
            m.payment_method_owner, m.owner, m.bank_name, m.bank,
            m.payment_method, m.bank_payment_code
          ].map((v) => String(v || '').toLowerCase()).join(' ');
          return haystack.includes('seylan bank');
        };
        const pick = modes.find(containsSeylanBank);
        if (!pick) return prev;
        const code = pick.bank_payment_code || pick.payment_method_code || pick.code || '';
        return code ? { ...prev, settlementAccountCode: code } : prev;
      });
    } catch (error) {
      console.error('Error fetching settlement modes:', error);
      setSettlementModes([]);
    }
  };

  const fetchIsins = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/isin-master`);
      if (response.data && response.data.success) {
        setIsinList(response.data.data || []);
      } else {
        setIsinList([]);
      }
    } catch (error) {
      console.error('Error fetching ISINs:', error);
      setIsinList([]);
    }
  };

  const fetchInvestmentApprovers = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/investment-approver-master`);
      setInvestmentApprovers(response.data || []);
    } catch (error) {
      console.error('Error fetching investment approvers:', error);
      setInvestmentApprovers([]);
    }
  };

  const fetchFundMovementSources = async () => {
    setFundMovementSourcesLoading(true);
    try {
      const params = new URLSearchParams();
      if (formData.counterpartyName) params.append('counterparty', formData.counterpartyName);
      const response = await api.get(`/fixed-deposit/fund-movement-sources?${params.toString()}`);
      setFundMovementSources(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching fund movement sources:', error);
      setFundMovementSources([]);
    } finally {
      setFundMovementSourcesLoading(false);
    }
  };

  const generateRequestNumber = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}${month}${day}`;

    setRequestCounter(prevCounter => {
      const nextCounter = prevCounter;
      const padded = String(nextCounter).padStart(3, '0');
      const requestNo = `FD${dateStr}${padded}`;

      setFormData(prev => ({
        ...prev,
        requestNo
      }));

      return nextCounter + 1;
    });
  };

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const url = '/fixed-deposit/requests';
      
      const response = await api.get(url);
      
      console.log('API Response:', response.data);
      console.log('Response length:', response.data?.length || 0);
      
      // Map API response to table format
      const mappedRequests = (response.data || []).map(request => ({
        id: request.id,
        requestNo: request.request_no || request.requestNo,
        counterparty: request.counterparty_name || request.counterparty_long_name || request.contact_person || 'N/A',
        instrumentType: request.instrument_type || 'N/A',
        amount: parseFloat(request.requested_amount || 0),
        valueDate: request.value_date ? new Date(request.value_date).toLocaleDateString('en-US', { 
          month: '2-digit', 
          day: '2-digit', 
          year: 'numeric' 
        }) : 'N/A',
        rawValueDate: request.value_date || null,
        status: request.status || 'Draft',
        submittedBy: request.submitted_by_name || request.username || 'N/A',
        portfolio: request.portfolio_name || request.portfolio_id || 'N/A'
      }));
      
      console.log('Mapped requests:', mappedRequests);
      setRequests(mappedRequests);
    } catch (error) {
      console.error('Error fetching requests:', error);
      console.error('Error response:', error.response?.data);
      setRequests([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // When counterparty is changed, also update contact person from Issuer Master
    if (name === 'counterpartyName') {
      const selected = counterparties.find(
        issuer =>
          String(issuer.issuer_id || issuer.id) === String(value)
      );

      setFormData(prev => ({
        ...prev,
        counterpartyName: value,
        contactPerson: selected?.contact_person || ''
      }));
      return;
    }

    // When approver is changed, auto-fill designation
    if (name === 'approverId') {
      const selectedApprover = investmentApprovers.find(
        approver => String(approver.id) === String(value)
      );

      setFormData(prev => ({
        ...prev,
        approverId: value,
        approverName: selectedApprover?.name || '',
        approverDesignation: selectedApprover?.designation || ''
      }));
      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDateChange = (name, date) => {
    setFormData(prev => ({
      ...prev,
      [name]: date
    }));
  };

  const toggleFundSourceDeal = (deal) => {
    const id = deal.id;
    setFormData(prev => {
      const ids = prev.fundSourceDealIds || [];
      const selected = ids.includes(id);
      const newIds = selected ? ids.filter(d => d !== id) : [...ids, id];
      const newSum = fundMovementSources
        .filter(d => newIds.includes(d.id))
        .reduce((sum, d) => sum + (parseFloat(d.face_value) || 0), 0);
      return {
        ...prev,
        fundSourceDealIds: newIds,
        partAmountFromSources: newSum > 0 ? String(newSum) : ''
      };
    });
  };

  const handleSave = async () => {
    try {
      // Part amount validation: cash + sources must equal requested amount
      if (formData.fundMovement === 'yes' && formData.fundMovementType === 'part_amount') {
        const requested = parseFloat(formData.requestedAmount) || 0;
        const cash = parseFloat(formData.partAmountCash) || 0;
        const fromSources = parseFloat(formData.partAmountFromSources) || 0;
        const sum = cash + fromSources;
        const tolerance = 0.01;
        if (!formData.requestedAmount || requested <= 0) {
          alert('Please enter Requested Amount.');
          return;
        }
        if (Math.abs(sum - requested) > tolerance) {
          alert('Part amount invalid: Amount in cash + Total from sources must equal Requested Amount.');
          return;
        }
        const ids = formData.fundSourceDealIds || [];
        if (ids.length === 0 || fromSources <= 0) {
          alert('Part amount invalid: Select at least one source deal.');
          return;
        }
      }

      const payload = {
        ...formData,
        status: 'pending',
        valueDate: formData.valueDate ? formData.valueDate.toISOString().split('T')[0] : null,
        maturityDate: formData.maturityDate ? formData.maturityDate.toISOString().split('T')[0] : null,
        dayBasis: formData.dayBasis === 364 ? 364 : 365,
        fundMovement: formData.fundMovement || null,
        fundMovementType: formData.fundMovementType || null,
        partAmountCash: formData.partAmountCash != null && formData.partAmountCash !== '' ? formData.partAmountCash : null,
        partAmountFromSources: formData.partAmountFromSources != null && formData.partAmountFromSources !== '' ? formData.partAmountFromSources : null,
        settlementAccountCode: formData.settlementAccountCode || null,
        fundSourceDealIds: Array.isArray(formData.fundSourceDealIds) ? formData.fundSourceDealIds : (formData.fundSourceDealIds ? [].concat(formData.fundSourceDealIds) : [])
      };

      const response = await api.post('/fixed-deposit/requests', payload);
      const requestId = response.data?.data?.id || response.data?.id || payload.request_no;
      setCreatedFixedDepositRequestId(requestId);
      setShowFixedDepositDocumentSection(true);
      alert('Saved successfully. Request submitted for authorization.');
      fetchRequests();
      // Reset form after successful save
      setFormData({
        portfolio: '',
        book: '',
        module: '',
        requestNo: '',
        fileNumber: '',
        status: '',
        counterpartyType: '',
        counterpartyName: '',
        contactPerson: '',
        requestRemarks: '',
        instrumentType: '',
        isin: '',
        currency: 'LKR',
        requestedAmount: '',
        targetYield: '',
        valueDate: null,
        maturityDate: null,
        dayBasis: 365,
        fundMovement: 'no',
        fundMovementType: '',
        partAmountCash: '',
        partAmountFromSources: '',
        settlementAccountCode: '',
        fundSourceDealIds: [],
        approvalCategory: '',
        approverId: '',
        approverName: '',
        approverDesignation: '',
        approvalLimitRequired: '',
        approverNotes: ''
      });
    } catch (error) {
      console.error('Error saving:', error);
      if (error.response?.status === 401) {
        alert('Authentication failed. Please log in again.');
      } else {
        alert('Failed to save: ' + (error.response?.data?.error || error.response?.data?.details || error.message));
      }
    }
  };

  const handleReset = () => {
    setFormData({
      portfolio: '',
      book: '',
      module: '',
      requestNo: '',
      fileNumber: '',
      status: '',
      counterpartyType: '',
      counterpartyName: '',
      contactPerson: '',
      requestRemarks: '',
      instrumentType: '',
      isin: '',
      currency: 'LKR',
      requestedAmount: '',
      targetYield: '',
      valueDate: null,
      maturityDate: null,
      dayBasis: 365,
      fundMovement: 'no',
      fundMovementType: '',
      partAmountCash: '',
      partAmountFromSources: '',
      settlementAccountCode: '',
      fundSourceDealIds: [],
      approverId: '',
      approverName: '',
      approverDesignation: '',
      approvalLimitRequired: '',
      approverNotes: ''
    });
    generateRequestNumber();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  // For authorizers (back_office_final or authorizer role), show the blotter instead of the entry form
  if (isAuthorizer) {
    return <BackOfficeFinalFixedDepositBlotter />;
  }

  return (
    <div className="fixed-deposit-page ui-page">
      {/* Request Details Row */}
      <div className="fd-request-details ui-card">
        <div className="fd-form-group">
          <label>Portfolio / Company</label>
          <select 
            name="portfolio" 
            value={formData.portfolio} 
            onChange={handleInputChange}
          >
            <option value="">Select Portfolio</option>
            {portfolios.map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>

        <div className="fd-form-group">
          <label>Book / Ledger</label>
          <select 
            name="book" 
            value={formData.book} 
            onChange={handleInputChange}
          >
            <option value="">Select Book</option>
            <option value="Investments">Investments</option>
          </select>
        </div>

        <div className="fd-form-group">
          <label>Module</label>
          <select
            name="module"
            value={formData.module}
            onChange={handleInputChange}
          >
            <option value="">Select Module</option>
            <option value="Pre approval">Pre approval</option>
            <option value="Approved">Approved</option>
          </select>
        </div>

        <div className="fd-form-group">
          <label>Request No.</label>
          <input 
            type="text" 
            name="requestNo" 
            value={formData.requestNo} 
            onChange={handleInputChange}
          />
        </div>

        <div className="fd-form-group">
          <label>File Number</label>
          <input 
            type="text" 
            name="fileNumber" 
            value={formData.fileNumber} 
            onChange={handleInputChange}
          />
        </div>

        <div className="fd-form-group">
          <label>Status</label>
          <select 
            name="status" 
            value={formData.status} 
            onChange={handleInputChange}
          >
            <option value="">Select Status</option>
            <option value="Draft">Draft</option>
            <option value="Pending">Pending</option>
            <option value="Approved">Approved</option>
            <option value="Returned">Returned</option>
          </select>
        </div>
      </div>

      {/* Three Column Layout */}
      <div className="fd-main-sections">
        {/* Left: Issuer / Counterparty Details */}
        <div className="fd-section-card ui-card">
          <h3>Issuer / Counterparty Details</h3>
          
          <div className="fd-form-group">
            <label>Counterparty: Name</label>
            <select 
              name="counterpartyName" 
              value={formData.counterpartyName} 
              onChange={handleInputChange}
            >
              <option value="">Select Counterparty</option>
              {counterparties.map(issuer => (
                <option
                  key={issuer.issuer_id || issuer.id}
                  value={issuer.issuer_id || issuer.id}
                >
                  {issuer.company_name}
                  {issuer.bank_name ? ` - ${issuer.bank_name}` : ''}
                  {issuer.bank_branch ? ` (${issuer.bank_branch})` : ''}
                </option>
              ))}
            </select>
          </div>

          <div className="fd-form-group">
            <label>Contact Person</label>
            <input 
              type="text" 
              name="contactPerson" 
              value={formData.contactPerson} 
              onChange={handleInputChange}
            />
          </div>

          <div className="fd-form-group">
            <label>Request Remarks</label>
            <input 
              type="text" 
              name="requestRemarks" 
              value={formData.requestRemarks} 
              onChange={handleInputChange}
            />
          </div>
        </div>

        {/* Middle: Investment Details */}
        <div className="fd-section-card ui-card">
          <h3>Investment Details</h3>
          
          <div className="fd-form-group">
            <label>Instrument Type</label>
            <select 
              name="instrumentType" 
              value={formData.instrumentType} 
              onChange={handleInputChange}
            >
              <option value="">Select Instrument Type</option>
              <option value="1-Btlnd">1-Btlnd</option>
              <option value="T Bond">T Bond</option>
              <option value="T Bill">T Bill</option>
              <option value="Fixed Deposit">Fixed Deposit</option>
            </select>
          </div>

          <div className="fd-form-group">
            <label>ISIN / Instrument Code</label>
            <select 
              name="isin" 
              value={formData.isin} 
              onChange={handleInputChange}
            >
              <option value="">Select ISIN</option>
              {isinList.map(isin => (
                <option key={isin.isin_number} value={isin.isin_number}>
                  {isin.isin_number} - {isin.isin_issuer || 'Security'}
                </option>
              ))}
            </select>
          </div>

          <div className="fd-form-group">
            <label>Currency</label>
            <select 
              name="currency" 
              value={formData.currency} 
              onChange={handleInputChange}
            >
              <option value="">Select Currency</option>
              <option value="LKR">LKR</option>
              <option value="USD">USD</option>
              <option value="EUR">EUR</option>
            </select>
          </div>

          <div className="fd-form-group">
            <label>Requested Amount</label>
            <input 
              type="number" 
              name="requestedAmount" 
              value={formData.requestedAmount} 
              onChange={handleInputChange}
              step="0.01"
            />
          </div>

          {/* Fund Movement */}
          <div className="fd-form-group">
            <label>Fund Movement</label>
            <select
              name="fundMovement"
              value={formData.fundMovement}
              onChange={handleInputChange}
            >
              <option value="no">No</option>
              <option value="yes">Yes</option>
            </select>
          </div>
          {formData.fundMovement === 'yes' && (
            <>
              <div className="fd-form-group">
                <label>Amount type</label>
                <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <input
                      type="radio"
                      name="fundMovementType"
                      value="full_amount"
                      checked={formData.fundMovementType === 'full_amount'}
                      onChange={handleInputChange}
                    />
                    Full Amount
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <input
                      type="radio"
                      name="fundMovementType"
                      value="part_amount"
                      checked={formData.fundMovementType === 'part_amount'}
                      onChange={handleInputChange}
                    />
                    Part Amount
                  </label>
                </div>
              </div>
              {formData.fundMovementType === 'part_amount' && (
                <>
                  <div className="fd-form-group">
                    <label>Amount in cash</label>
                    <input
                      type="number"
                      name="partAmountCash"
                      value={formData.partAmountCash}
                      onChange={handleInputChange}
                      step="0.01"
                      placeholder="Enter cash amount"
                    />
                  </div>
                  <div className="fd-form-group">
                    <label>Settlement A/C</label>
                    <select
                      name="settlementAccountCode"
                      value={formData.settlementAccountCode}
                      onChange={handleInputChange}
                    >
                      <option value="">Select Settlement Account</option>
                      {(Array.isArray(settlementModes) ? settlementModes : []).map((mode) => (
                        <option
                          key={mode.bank_payment_code || mode.id || mode.payment_method}
                          value={mode.bank_payment_code || mode.id || mode.payment_method}
                        >
                          {mode.payment_method ? `${mode.payment_method}${mode.bank_payment_code ? ` - ${mode.bank_payment_code}` : ''}` : (mode.name || mode.id || '')}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="fd-form-group">
                    <label>Balance from existing FD (select source deals)</label>
                    {fundMovementSourcesLoading ? (
                      <span>Loading sources...</span>
                    ) : fundMovementSources.length === 0 ? (
                      <span style={{ color: '#666' }}>No matured or pre-approved FD deals available.</span>
                    ) : (
                      <div className="fd-fund-sources-table-wrap">
                        <table className="fd-requests-table ui-table" style={{ marginTop: '8px' }}>
                          <thead>
                            <tr>
                              <th>Select</th>
                              <th>Deal No</th>
                              <th>Counterparty</th>
                              <th>Face Value</th>
                              <th>Maturity Date</th>
                              <th>Source</th>
                            </tr>
                          </thead>
                          <tbody>
                            {fundMovementSources.map((deal) => (
                              <tr key={deal.id}>
                                <td>
                                  <input
                                    type="checkbox"
                                    checked={(formData.fundSourceDealIds || []).includes(deal.id)}
                                    onChange={() => toggleFundSourceDeal(deal)}
                                  />
                                </td>
                                <td>{deal.deal_number || deal.request_no}</td>
                                <td>{deal.counterparty_name || 'N/A'}</td>
                                <td>{formatCurrency(deal.face_value || 0)}</td>
                                <td>{deal.maturity_date ? new Date(deal.maturity_date).toLocaleDateString() : 'N/A'}</td>
                                <td>{deal.source_type === 'matured' ? 'Matured' : 'Pre-approved'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {(formData.partAmountFromSources || 0) > 0 && (
                          <p style={{ marginTop: '8px', fontWeight: 'bold', color: '#0a0' }}>
                            Total from sources: {formatCurrency(parseFloat(formData.partAmountFromSources) || 0)}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </>
              )}
            </>
          )}

          <div className="fd-form-group">
            <label>Target Yield</label>
            <input 
              type="number" 
              name="targetYield" 
              value={formData.targetYield} 
              onChange={handleInputChange}
              step="0.01"
            />
          </div>

          <div className="fd-form-group">
            <label>Value Date</label>
            <input
              type="date"
              value={formData.valueDate ? formData.valueDate.toISOString().split('T')[0] : ''}
              onChange={(e) => handleDateChange('valueDate', e.target.value ? new Date(e.target.value) : null)}
              className="form-control fd-date-input"
            />
          </div>

          <div className="fd-form-group">
            <label>Maturity Date</label>
            <input
              type="date"
              value={formData.maturityDate ? formData.maturityDate.toISOString().split('T')[0] : ''}
              onChange={(e) => handleDateChange('maturityDate', e.target.value ? new Date(e.target.value) : null)}
              className="form-control fd-date-input"
            />
          </div>

          <div className="fd-form-group">
            <label>Day Basis</label>
            <select
              name="dayBasis"
              value={formData.dayBasis === 364 ? 364 : 365}
              onChange={(e) => setFormData(prev => ({ ...prev, dayBasis: e.target.value === '364' ? 364 : 365 }))}
              className="form-control"
            >
              <option value={365}>365</option>
              <option value={364}>364</option>
            </select>
          </div>

          {formData.requestedAmount && formData.targetYield && (formData.dayBasis === 364 || formData.dayBasis === 365) && (() => {
            const p = parseFloat(formData.requestedAmount);
            const y = parseFloat(formData.targetYield);
            const basis = formData.dayBasis === 364 ? 364 : 365;
            if (!isNaN(p) && p > 0 && !isNaN(y)) {
              const daily = (p * y / 100) / basis;
              return (
                <div className="fd-form-group">
                  <label>Daily Accrual (preview)</label>
                  <input type="text" readOnly value={daily.toFixed(8)} className="form-control" style={{ backgroundColor: 'var(--bg-subtle)' }} />
                </div>
              );
            }
            return null;
          })()}
        </div>

        {/* Right: Approval Workflow */}
        <div className="fd-section-card ui-card">
          <h3>Approval Workflow</h3>
          
          <div className="fd-form-group">
            <label>Approver</label>
            <select 
              name="approverId" 
              value={formData.approverId} 
              onChange={handleInputChange}
            >
              <option value="">Select Approver</option>
              {investmentApprovers.map(approver => (
                <option key={approver.id} value={approver.id}>
                  {approver.name}
                </option>
              ))}
            </select>
          </div>

          <div className="fd-form-group">
            <label>Designation</label>
            <input 
              type="text" 
              name="approverDesignation" 
              value={formData.approverDesignation} 
              onChange={handleInputChange}
              readOnly
              placeholder="Designation will be auto-filled"
            />
          </div>

          <div className="fd-form-group">
            <label>Approval Limit Required</label>
            <input 
              type="text" 
              name="approvalLimitRequired" 
              value={formData.approvalLimitRequired} 
              onChange={handleInputChange}
              placeholder="Enter limit"
            />
          </div>

          <div className="fd-form-group">
            <label>Approver Notes</label>
            <textarea 
              name="approverNotes" 
              value={formData.approverNotes} 
              onChange={handleInputChange}
              rows="4"
              placeholder="Enter approver notes"
            />
          </div>
        </div>
      </div>

      {/* Bottom Section: Table */}
      <div className="fd-bottom-section">
        <div className="fd-recent-deals-header" style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '15px', flexWrap: 'wrap' }}>
          <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>Recent Deals</h3>
          <div className="fd-form-group" style={{ margin: 0, minWidth: '180px' }}>
            <label style={{ marginBottom: '4px' }}>Filter by Value Date</label>
            <input
              type="date"
              value={recentDealsFilterDate.toISOString().split('T')[0]}
              onChange={(e) => setRecentDealsFilterDate(e.target.value ? new Date(e.target.value) : new Date())}
              className="form-control fd-date-input"
            />
          </div>
        </div>
        <div className="fd-table-container ui-table-wrap">
          <table className="fd-requests-table ui-table">
            <thead>
              <tr>
                <th>Request No</th>
                <th>Counterparty</th>
                <th>Instrument Type</th>
                <th>Amount</th>
                <th>Value Date</th>
                <th>Status</th>
                <th>Submitted By</th>
                <th>Portfolio / Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="8" style={{ textAlign: 'center' }}>Loading...</td>
                </tr>
              ) : (() => {
                const sameDay = (a, b) => {
                  if (!a || !b) return false;
                  const d1 = new Date(a);
                  const d2 = new Date(b);
                  return d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth() && d1.getDate() === d2.getDate();
                };
                const filtered = requests.filter((r) => r.rawValueDate ? sameDay(r.rawValueDate, recentDealsFilterDate) : false);
                if (filtered.length === 0) {
                  return (
                    <tr>
                      <td colSpan="8" style={{ textAlign: 'center' }}>No requests found for this date</td>
                    </tr>
                  );
                }
                return filtered.map((request) => (
                  <tr key={request.id || request.requestNo || `request-${request.requestNo}`}>
                    <td>{request.requestNo || 'N/A'}</td>
                    <td>{request.counterparty || 'N/A'}</td>
                    <td>{request.instrumentType || 'N/A'}</td>
                    <td>{request.amount ? formatCurrency(request.amount) : 'N/A'}</td>
                    <td>{request.valueDate || 'N/A'}</td>
                    <td>
                      <span className={`status-badge status-${(request.status || 'Draft').toLowerCase()}`}>
                        {request.status || 'Draft'}
                      </span>
                    </td>
                    <td>{request.submittedBy || 'N/A'}</td>
                    <td>
                      {request.portfolio || 'N/A'}
                      <button 
                        className="fd-btn fd-btn-sm" 
                        onClick={() => setSelectedRequestForDocuments(request)}
                        style={{ marginLeft: '8px', padding: '4px 8px', fontSize: '12px' }}
                        title="View/Upload Documents"
                      >
                        📎 Docs
                      </button>
                    </td>
                  </tr>
                ));
              })()}
            </tbody>
          </table>
        </div>

        {/* Action Buttons */}
        <div className="fd-action-buttons">
          <button className="fd-btn fd-btn-primary ui-btn ui-btn-primary" onClick={handleSave}>
            Save
          </button>
          <button className="fd-btn fd-btn-secondary ui-btn ui-btn-secondary" onClick={handleReset}>
            Reset
          </button>
        </div>
      </div>
      
      {/* Document Upload Section - Show after request is created or when viewing existing request */}
      {(showFixedDepositDocumentSection && createdFixedDepositRequestId) || selectedRequestForDocuments ? (
        <div style={{ marginTop: '24px', width: '100%', padding: '20px', border: '1px solid #e0e0e0', borderRadius: '8px', background: '#fff' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h4 style={{ margin: 0 }}>
              Documents
              {(createdFixedDepositRequestId || selectedRequestForDocuments?.id || selectedRequestForDocuments?.request_no) && (
                <span style={{ fontSize: '14px', fontWeight: 'normal', color: '#666', marginLeft: '10px' }}>
                  - Request #: {createdFixedDepositRequestId || selectedRequestForDocuments?.id || selectedRequestForDocuments?.request_no}
                </span>
              )}
            </h4>
            {selectedRequestForDocuments && (
              <button 
                className="fd-btn fd-btn-secondary" 
                onClick={() => setSelectedRequestForDocuments(null)}
              >
                Close
              </button>
            )}
          </div>
          <DocumentUpload
            transactionType="fixed_deposit"
            transactionId={createdFixedDepositRequestId || selectedRequestForDocuments?.id || selectedRequestForDocuments?.request_no}
            onUploadSuccess={(documents) => {
              setShowFixedDepositDocumentSection(false);
            }}
            onUploadError={(error) => {
              console.error('Failed to upload document:', error);
            }}
            multiple={true}
          />
          <DocumentList
            transactionType="fixed_deposit"
            transactionId={createdFixedDepositRequestId || selectedRequestForDocuments?.id || selectedRequestForDocuments?.request_no}
            onDocumentDeleted={() => {}}
          />
        </div>
      ) : null}
    </div>
  );
};

export default FixedDepositPage;
